import { ArrowDown } from 'lucide-react';

import { cn } from '@rocco/utils/cn';

/**
 * Load More (Figma 149:13459) — a white bar centred under a grid, the label at
 * the left of it and the arrow at the right, lifted on a wide soft shadow.
 *
 * Replaces the dark `filled-light` pill every paginated block on this page
 * used to carry: the same control in the same place, said quietly, because
 * "there is more below" is not the page's primary action.
 */
const CLASS = cn(
    'group flex cursor-pointer items-center justify-between rounded-[1.375rem] bg-white',
    'font-sans font-medium text-black transition-colors hover:bg-white/70',
    '[box-shadow:0px_4px_14px_10px_rgba(0,0,0,0.05)]',
    'disabled:cursor-default disabled:opacity-50',
    'max-md:h-[3.25rem] max-md:w-full max-md:pr-5 max-md:pl-4 max-md:text-base',
    // The L step (52px), where the other reveal-more controls sit — the
    // downloads' Show all and the profile's View all Projects.
    'md:h-[3.25rem] md:w-[14.375rem] md:pr-[1.625rem] md:pl-[1rem] md:text-[1rem]',
);

interface LoadMoreButtonProps {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    isLoading?: boolean;
    /** Spacing above the button, which differs per block. */
    className?: string;
    /** The ground it sits on. The project detail page is dark. */
    tone?: 'light' | 'dark';
    /**
     * Renders the control as a link to the given page rather than a button.
     *
     * The paginated editorial grids are crawlable — their next page is a real
     * `?page=` address and has to stay one — and the click only takes over to
     * load the rows in place. Everywhere else there is no address for the next
     * rows, so the control is a button.
     */
    href?: string;
    /** Overrides "Load More" — e.g. a count, or a collapse. */
    label?: string;
    /**
     * Spoken label, where the visible one does not say what is being loaded.
     * A grid footer reading "View More" three screens from its heading is the
     * case: on its own it names neither the thing nor the page.
     */
    ariaLabel?: string;
    /**
     * Turns the arrow over, for a control that reveals in place and can put
     * the rows back. Pagination only goes one way and leaves this unset.
     */
    expanded?: boolean;
}

export const LoadMoreButton = ({
    onClick,
    isLoading = false,
    className,
    tone = 'light',
    href,
    label,
    ariaLabel,
    expanded,
}: LoadMoreButtonProps) => {
    const text = isLoading ? 'Loading…' : (label ?? 'Load More');
    const inner = (
        <>
            {text}
            <ArrowDown
                aria-hidden="true"
                className={cn(
                    'shrink-0 max-md:size-5 md:size-[1.28125rem]',
                    expanded
                        ? // One arrow, turned over. A separate chevron would
                          // read as one control replaced by another; turning
                          // this one reads as the same control changing its
                          // mind. It rises on hover, where the press sends it.
                          [
                              'rotate-180 transition-transform duration-200',
                              'group-hover:-translate-y-[0.1875rem]',
                              'motion-reduce:transition-none',
                          ]
                        : // Plays once on hover, pointing at what the press does.
                          [
                              'group-hover:[animation:load-more-nudge_0.45s_ease-out]',
                              'motion-reduce:group-hover:[animation:none]',
                          ],
                )}
            />
        </>
    );
    const classes = cn(
        CLASS,
        // The platform's one dark ink, as the button system fills it.
        tone === 'dark' &&
            'bg-[#323232] text-white shadow-none hover:bg-[#4a4a4a]',
    );

    return (
        <div className={cn('flex justify-center', className)}>
            {href ? (
                <a
                    href={href}
                    onClick={(event) => {
                        // `pointer-events-none` takes the pointer away but
                        // leaves the link on the tab order, where Enter still
                        // follows it; the loading state has to refuse the
                        // press itself.
                        if (isLoading) {
                            event.preventDefault();
                            return;
                        }
                        onClick(event);
                    }}
                    aria-disabled={isLoading}
                    aria-label={ariaLabel}
                    className={cn(
                        classes,
                        'no-underline',
                        isLoading && 'pointer-events-none opacity-50',
                    )}
                >
                    {inner}
                </a>
            ) : (
                <button
                    type="button"
                    onClick={onClick}
                    disabled={isLoading}
                    aria-label={ariaLabel}
                    className={classes}
                >
                    {inner}
                </button>
            )}
        </div>
    );
};
